import type { ApiUser, AthleteItem, UserProfile } from '../services/api';
import { mapApiUserToProfile, user } from '../services/api';

/** Parses `/me` and login profile payloads that may nest `user`. */
export function parseApiUserPayload(raw: unknown): ApiUser {
  if (raw && typeof raw === 'object' && 'user' in raw && (raw as { user?: unknown }).user) {
    return (raw as { user: ApiUser }).user;
  }
  return raw as ApiUser;
}

/** Keeps coach role and athletes when a partial API response omits them. */
export function mergeUserProfiles(
  previous: UserProfile | null | undefined,
  fresh: UserProfile,
): UserProfile {
  const prevAthletes = previous?.athletes ?? [];
  const freshAthletes = fresh.athletes ?? [];
  const athletes =
    freshAthletes.length > 0
      ? freshAthletes
      : prevAthletes.length > 0
        ? prevAthletes
        : [];

  const role = fresh.role ?? previous?.role;

  return {
    ...fresh,
    role,
    athletes,
  };
}

/** electron-store rejects undefined values — strip them before persistence. */
export function sanitizeUserForStore(profile: UserProfile): Record<string, unknown> {
  return JSON.parse(JSON.stringify(profile)) as Record<string, unknown>;
}

/** Loads stored profile shape from electron-store (may be partial). */
export function storedRecordToProfile(raw: Record<string, unknown> | null | undefined): UserProfile | null {
  if (!raw || typeof raw !== 'object' || typeof raw.id !== 'number') {
    return null;
  }
  return mapApiUserToProfile(raw as unknown as ApiUser);
}

/**
 * Loads the full coach athlete roster (primary + shared/assistant).
 * Mobile always calls GET /user/get-athletes; Bridge must do the same — `/me` alone
 * used to expose only primary athletes via the coached_by relation.
 */
export async function enrichCoachAthletes(profile: UserProfile): Promise<UserProfile> {
  if (profile.role !== 'coach') {
    return profile;
  }

  try {
    const res = await user.getAthletes();
    const athletes: AthleteItem[] = (res.data.data.athletes ?? []).map((a) => ({
      id: a.id,
      full_name: a.full_name,
      profile_image: a.profile_image,
      coaching_relationship: a.coaching_relationship,
      last_workout_uploaded_at: a.last_workout_uploaded_at,
    }));
    return { ...profile, athletes };
  } catch {
    return profile;
  }
}
